import React from 'react';
import { Box, TextField, Chip, Autocomplete, Checkbox } from '@mui/material';
import type { BasketballSettings, BasketballTieBreaker } from '@/types';
import type { SportSettingsFormProps } from './types';
import { SettingsSection } from './SettingsSection';
import { SettingsField, SettingsFieldsGrid } from './SettingsFieldsGrid';

const TIEBREAKER_OPTIONS: { value: BasketballTieBreaker; label: string }[] = [
  { value: 'point_difference', label: 'Разница очков' },
  { value: 'points_scored',    label: 'Набранные очки' },
  { value: 'head_to_head',     label: 'Личные встречи' },
];

export const BasketballSettingsForm: React.FC<SportSettingsFormProps> = ({ value, onChange, disabled }) => {
  const s = value as BasketballSettings;
  const upd = (patch: Partial<BasketballSettings>) => onChange({ ...s, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <SettingsSection title="Состав и структура">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Игроков на площадке" type="number" size="small" fullWidth
              value={s.playersOnCourt ?? 5}
              onChange={(e) => upd({ playersOnCourt: parseInt(e.target.value) || 5 })}
              slotProps={{ htmlInput: { min: 1, max: 5 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Четвертей" type="number" size="small" fullWidth
              value={s.quarters ?? 4}
              onChange={(e) => upd({ quarters: parseInt(e.target.value) || 4 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Длина четверти, мин" type="number" size="small" fullWidth
              value={s.quarterDurationMin ?? 10}
              onChange={(e) => upd({ quarterDurationMin: parseInt(e.target.value) || 10 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Длина ОТ, мин" type="number" size="small" fullWidth
              value={s.overtimeDurationMin ?? 5}
              onChange={(e) => upd({ overtimeDurationMin: parseInt(e.target.value) || 5 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Лимит фолов" type="number" size="small" fullWidth
              value={s.foulLimit ?? 5}
              onChange={(e) => upd({ foulLimit: parseInt(e.target.value) || 5 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Очки">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="За победу" type="number" size="small" fullWidth
              value={s.pointsWin ?? 2}
              onChange={(e) => upd({ pointsWin: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="За поражение" type="number" size="small" fullWidth
              value={s.pointsLoss ?? 0}
              onChange={(e) => upd({ pointsLoss: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Тай-брейки">
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={TIEBREAKER_OPTIONS.map((o) => o.value)}
          value={s.tieBreakers ?? []}
          onChange={(_, v) => upd({ tieBreakers: v as BasketballTieBreaker[] })}
          getOptionLabel={(o) => TIEBREAKER_OPTIONS.find((x) => x.value === o)?.label ?? o}
          disabled={disabled}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
              {TIEBREAKER_OPTIONS.find((x) => x.value === option)?.label ?? option}
            </li>
          )}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip label={`${idx + 1}. ${TIEBREAKER_OPTIONS.find((x) => x.value === opt)?.label ?? opt}`}
                size="small" {...getTagProps({ index: idx })} key={opt} />
            ))
          }
          renderInput={(params) => <TextField {...params} size="small" fullWidth placeholder="Добавить критерий..." />}
        />
      </SettingsSection>

    </Box>
  );
};
